// Service de cache centralisé pour optimiser les performances
import { api } from '@/api'
import type { GraffitiFromAPI, GraffitiWithThemes } from '@/types'
import { convertGraffitiFromAPI } from '@/types'

interface CacheEntry<T> {
  data: T
  timestamp: number
  expiresAt: number
}

class CacheService {
  private cache = new Map<string, CacheEntry<unknown>>()
  private readonly DEFAULT_TTL = 5 * 60 * 1000 // 5 minutes
  private readonly LONG_TTL = 30 * 60 * 1000 // 30 minutes pour les données statiques

  // Générer une clé de cache
  private getCacheKey(type: string, params?: Record<string, unknown>): string {
    const paramsStr = params ? JSON.stringify(params) : ''
    return `${type}:${paramsStr}`
  }

  // Vérifier si un cache est valide
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() < entry.expiresAt
  }

  // Méthode générique pour get/set avec cache
  private async getOrSet<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.DEFAULT_TTL
  ): Promise<T> {
    const cached = this.cache.get(key)

    if (cached && this.isValid(cached)) {
      ////console.log(`[CacheService] Cache HIT pour ${key}`)
      return cached.data as T
    }

    //console.log(`[CacheService] Cache MISS pour ${key} - Fetching...`)
    const data = await fetcher()

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    })

    return data
  }

  // Cache des graffitis complets (utilisé par l'exploreur)
  async getAllGraffitis(): Promise<GraffitiWithThemes[]> {
    const key = this.getCacheKey('graffitis:all')

    return this.getOrSet(
      key,
      async () => {
        const apiData = await api.graffitis.getAll()
        return apiData.map(convertGraffitiFromAPI)
      },
      this.LONG_TTL // Cache plus long pour les données principales
    )
  }

  // Cache d'un graffiti individuel (page détail)
  async getGraffitiById(id: number): Promise<GraffitiWithThemes | null> {
    const key = this.getCacheKey('graffiti:detail', { id })

    return this.getOrSet(key, async () => {
      try {
        const data = await api.graffitis.getById(id)
        return data
      } catch (_error) {
        console.warn(`[CacheService] Graffiti ${id} not found in API`)
        return null
      }
    })
  }

  // Cache d'un graffiti avec liens (page détail complète)
  async getGraffitiWithDetails(id: number): Promise<GraffitiFromAPI | null> {
    const key = this.getCacheKey('graffiti:full-detail', { id })

    return this.getOrSet(key, async () => {
      try {
        // Fallback vers graffitiService si l'API ne fournit pas les détails complets
        const { graffitiService } = await import('@/services/graffitiService')
        return await graffitiService.getGraffitiWithDetails(id)
      } catch (_error) {
        console.warn(`[CacheService] Graffiti details ${id} not found`)
        return null
      }
    })
  }

  // Cache des graffitis par thématique
  async getGraffitisByTheme(theme: string): Promise<GraffitiWithThemes[]> {
    const key = this.getCacheKey('graffitis:theme', { theme })

    return this.getOrSet(key, async () => {
      const allGraffitis = await this.getAllGraffitis()
      return allGraffitis.filter(graffiti =>
        graffiti.themes.some((t: string) => t.toLowerCase() === theme.toLowerCase())
      )
    })
  }

  // Cache des graffitis par catégorie
  async getGraffitisByCategory(category: string): Promise<GraffitiWithThemes[]> {
    const key = this.getCacheKey('graffitis:category', { category })

    return this.getOrSet(key, async () => {
      const allGraffitis = await this.getAllGraffitis()
      return allGraffitis.filter(
        graffiti =>
          graffiti.categorie && graffiti.categorie.toLowerCase() === category.toLowerCase()
      )
    })
  }

  // Cache des catégories uniques
  async getAllCategories(): Promise<string[]> {
    const key = this.getCacheKey('categories:all')

    return this.getOrSet(
      key,
      async () => {
        const allGraffitis = await this.getAllGraffitis()
        const categories = [
          ...new Set(
            allGraffitis
              .map(g => g.categorie)
              .filter((c): c is string => c !== undefined && c !== null)
          ),
        ]
        return categories.sort()
      },
      this.LONG_TTL
    )
  }

  // Cache des thématiques uniques
  async getAllThemes(): Promise<string[]> {
    const key = this.getCacheKey('themes:all')

    return this.getOrSet(
      key,
      async () => {
        const allGraffitis = await this.getAllGraffitis()
        const themes = [
          ...new Set(
            allGraffitis
              .flatMap(g => g.themes)
              .filter((t): t is string => t !== undefined && t !== null)
          ),
        ]
        return themes.sort()
      },
      this.LONG_TTL
    )
  }

  // Cache des langues uniques
  async getAllLanguages(): Promise<string[]> {
    const key = this.getCacheKey('languages:all')

    return this.getOrSet(
      key,
      async () => {
        const allGraffitis = await this.getAllGraffitis()
        const languages = [
          ...new Set(
            allGraffitis
              .map(g => g.langue)
              .filter((l): l is string => l !== undefined && l !== null)
          ),
        ]
        return languages.sort()
      },
      this.LONG_TTL
    )
  }

  // Invalider un cache spécifique
  invalidate(pattern: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(pattern))

    keysToDelete.forEach(key => {
      this.cache.delete(key)
      //console.log(`[CacheService] Cache invalidé : ${key}`)
    })
  }

  // Invalider tout le cache
  invalidateAll(): void {
    this.cache.clear()
    //console.log('[CacheService] Tout le cache a été invalidé')
  }

  // Nettoyer les caches expirés
  cleanup(): void {
    const now = Date.now()
    let cleanedCount = 0

    for (const [key, entry] of this.cache.entries()) {
      if (now >= entry.expiresAt) {
        this.cache.delete(key)
        cleanedCount++
      }
    }

    if (cleanedCount > 0) {
      //console.log(`[CacheService] ${cleanedCount} entrées de cache expirées nettoyées`)
    }
  }

  // Statistiques du cache
  getStats() {
    const stats = {
      totalEntries: this.cache.size,
      validEntries: 0,
      expiredEntries: 0,
    }

    const now = Date.now()
    for (const entry of this.cache.values()) {
      if (now < entry.expiresAt) {
        stats.validEntries++
      } else {
        stats.expiredEntries++
      }
    }

    return stats
  }
}

// Instance singleton
export const cacheService = new CacheService()

// Nettoyage automatique toutes les 5 minutes
setInterval(
  () => {
    cacheService.cleanup()
  },
  5 * 60 * 1000
)
